/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import {
    announce,
    Button,
    ButtonGroup,
    CartActions,
    DisplayHtmlContent,
    ModalActions,
    Stack,
    StyledModal,
    Text,
} from 'sarsaparilla';

import { getMaxTotalGuests, getTimeOptions } from '../../utils/generalTicketUtils';
import {
    getGuestCounterOptions,
    getMaxTotalGuestsByGuestType,
    getMinTotalGuestsByGuestType,
} from '../../utils/guestTypesUtils';
import { convertServerErrorToDisplayError } from '../../utils/convertServerErrorToDisplayError';

import { setDateFilter } from '../../actions/filtersActions';
import {
    closeModifyModal,
    getOrderDetailsView,
    updateWithTicketHolderDetails,
} from '../../actions/orderDetailsActions';

import { ModifyReservationForm } from '../../components/OrderDetailsPage/ModifyReservationForm';

function getTotalFromCountByGuestId(ticketCountByGuestTypeId) {
    let totalCountFromReservation = 0;
    if (!isEmpty(ticketCountByGuestTypeId)) {
        for (const key in ticketCountByGuestTypeId) {
            if (typeof ticketCountByGuestTypeId[key] === 'number') {
                totalCountFromReservation += ticketCountByGuestTypeId[key];
            }
        }
    }
    return totalCountFromReservation;
}

/**
 * Helper function to generate initial GuestCounter value state
 */
function getInitialCounts({ allowedGuestTypeIds, ticketCountByGuestTypeId }) {
    const initialCounts = {};

    if (allowedGuestTypeIds) {
        allowedGuestTypeIds.forEach((guestTypeId) => {
            initialCounts[guestTypeId] = ticketCountByGuestTypeId[guestTypeId] || 0;
        });
    }

    return initialCounts;
}

/**
 * Helper function to get the number of available tickets for a given tour time
 */
function getAvailableCount({ tourTimes, time, dateM, tourDate, tourTime, totalGuests }) {
    const newDate = dateM ? dateM?.format('YYYY-MM-DD') : null;

    let availableCount = 0;

    if (tourTimes && time) {
        const selectedTourTime =
            tourTimes.length > 1
                ? tourTimes.filter((tour) => tour.time === time)
                : tourTimes[0];
        availableCount = selectedTourTime?.[0]?.availableCount || 0;
    }

    // The true available count is the availability *plus* the number the user has
    // added to their cart
    if (time === tourTime && newDate === tourDate) {
        availableCount += totalGuests;
    }
    return availableCount;
}

function filterCounts(counts) {
    const filteredCounts = { ...counts };

    for (const property in counts) {
        if (!counts[property]) {
            delete filteredCounts[property];
        }
    }
    return filteredCounts;
}

function getAdjustedTourTimes({ tourTimes, tourDate, dateM, tourTime, totalGuests }) {
    const newDate = dateM ? dateM?.format('YYYY-MM-DD') : null;

    if (newDate === tourDate) {
        const newTourTimes = tourTimes.map((item) => {
            const newItem = { ...item };
            if (item.time === tourTime) {
                newItem.availableCount += totalGuests;
            }
            return newItem;
        });

        return newTourTimes;
    }

    return tourTimes;
}

/******************************************
 * Container for the modify modal and form
 *****************************************/
export function ModifyModal({
    hasAnotherReservationForCurrentDate,
    availableSitePassForSelectedDate,
    sitePassForCurrentDate,
}) {
    const dispatch = useDispatch();

    // Data from redux
    const reservation =
        useSelector((state) => state.timedEntry.orderDetails?.reservation) || {};
    const dateM = useSelector((state) => state.timedEntry.filters?.date);
    const tourTimesData = useSelector((state) => state.timedEntry.tourTimes?.data);
    const isTourTimesLoading = useSelector(
        (state) => state.timedEntry.tourTimes.isLoading
    );
    const facilityData = useSelector((state) => state.timedEntry.facility);
    const tourMap = useSelector((state) => state.timedEntry.tours?.tourMap);
    const orderDetailsData = useSelector((state) => state.timedEntry.orderDetails);
    const accountType = useSelector((state) => state.timedEntry.accountType.accountType);

    // Derived state
    const {
        tour_id: tourId,
        // tour_time: tourTime,
        // tour_date: tourDate,
        reservation_id: reservationId,
    } = reservation;

    const tour = useSelector((state) =>
        state?.timedEntry?.tours?.tourList?.find(
            (tourItem) => tourItem?.tour_id === tourId
        )
    );

    const tourTime = reservation?.pending_modifications?.tour_time
        ? reservation?.pending_modifications?.tour_time
        : reservation.tour_time;

    const tourDate = reservation?.pending_modifications?.tour_date
        ? reservation?.pending_modifications?.tour_date
        : reservation.tour_date;

    const ticketCountByGuestTypeId =
        reservation?.pending_modifications?.ticket_count_by_guest_type_id ??
        reservation.ticket_count_by_guest_type_id;

    const { isModifyModalOpen } = orderDetailsData;
    const tourTimes = tourTimesData?.[tourId] || [];
    const tourData = tourMap?.[tourId] || {};
    const {
        guest_types: allGuestTypes = [],
        facility_id: reduxFacilityId,
        facility_timezone: timezone,
    } = facilityData;
    const {
        allowed_guest_type_ids: allowedGuestTypeIds = [],
        purchase_minimums_by_guest_type: purchaseMinimumsByGuestType,
        purchase_maximums_by_guest_type: purchaseMaximumsByGuestType,
        calculate_booking_window_close: bookThroughoutTourDuration,
    } = tourData;
    const totalGuests = getTotalFromCountByGuestId(ticketCountByGuestTypeId);
    const adjustedTourTimes = getAdjustedTourTimes({
        tourTimes,
        tourDate,
        tourTime,
        dateM,
        totalGuests,
    });

    // Get guest types array for GuestCounter
    const guestTypes = getGuestCounterOptions({
        guestTypes: allGuestTypes,
        allowedGuestTypeIds,
    });

    const isReservationConfirmed =
        reservation?.status === 'CONFIRMED' || reservation?.status === 'DELIVERED';

    const minTotalGuestsByGuestType = getMinTotalGuestsByGuestType(
        purchaseMinimumsByGuestType
    );
    const maxTotalGuestsByGuestType = getMaxTotalGuestsByGuestType(
        purchaseMaximumsByGuestType
    );

    // Local state
    const [counts, setCounts] = useState(ticketCountByGuestTypeId);
    const [time, setTime] = useState(tourTime);
    const [isSpinning, setSpinning] = useState(false);
    const [bookingError, setBookingError] = useState(false);

    const maxTotalGuests = getMaxTotalGuests({
        facility: facilityData,
        tour: tourData,
        accountType,
    });

    let countTotal = 0;
    for (const key in counts) {
        if (typeof counts[key] === 'number') {
            countTotal += counts[key];
        }
    }

    const getFormattedHtmlHelper = (
        displayDuration,
        availableCount,
        availableWalkupCount,
        unreleasedCount,
        hideAvailableCount,
        onlyHasWalkupAvailability
    ) => {
        let availableText = `(${availableCount || unreleasedCount || availableWalkupCount} left)`;
        let firstComeFirstServedClass = '';
        if (onlyHasWalkupAvailability) {
            availableText = `<span class="sarsa-text weight-bold">FF</span>&nbsp;only ${availableText}`;
            firstComeFirstServedClass = ' te-radio-pill--first-come-first-served';
        }
        const availableHtml = !hideAvailableCount
            ? `<div class="te-radio-pill-available">${availableText}</div>`
            : '';
        const labelText = `
            <div class="te-radio-pill${firstComeFirstServedClass}" aria-hidden="true">
                <div class="te-radio-pill-time">${displayDuration}</div>
                ${availableHtml}
            </div>
        `;
        return <DisplayHtmlContent html={labelText} />;
    };

    const calculateBookingWindowClose = tourData?.calculate_booking_window_close;

    // Create the RadioPillGroup options
    const { timeOptions: timeOptionsRaw } = getTimeOptions({
        tourTimes,
        countTotal,
        timezone,
        calculateBookingWindowClose,
    });
    const startText = bookThroughoutTourDuration ? 'Entry Window' : 'Starting Time';

    const timeOptions = timeOptionsRaw
        ?.map((item) => {
            const onlyHasWalkupAvailability =
                !item?.availableCount &&
                !item?.unreleasedCount &&
                !!item?.availableWalkupCount;

            const explanationText = onlyHasWalkupAvailability
                ? ' (This tickets are First-come, first-served and may be available once you are physically on site at this location, but there is no guarantee.)'
                : '';

            return {
                ...item,
                label: getFormattedHtmlHelper(
                    item?.displayDuration,
                    item?.availableCount,
                    item?.availableWalkupCount,
                    item?.unreleasedCount,
                    item?.hideAvailableCount,
                    onlyHasWalkupAvailability
                ),
                ariaLabel: `${startText} ${item?.displayDuration} with ${item?.availableCount || item?.unreleasedCount || item?.availableWalkupCount} left${explanationText}`,
            };
        })
        .filter(
            (timeOption) =>
                timeOption?.availableCount > 0 ||
                timeOption?.unreleasedCount > 0 ||
                timeOption?.availableWalkupCount > 0
        );

    // Get the available ticket counts
    const availableCount = getAvailableCount({
        tourTimes,
        time,
        dateM,
        tourDate,
        tourTime,
        totalGuests,
    });

    const hasBeenModified =
        tourTime !== time ||
        tourDate !== dateM?.format('YYYY-MM-DD') ||
        !isEqual(filterCounts(counts), ticketCountByGuestTypeId);

    const isFormInvalid = !time || !dateM || !getTotalFromCountByGuestId(counts);

    const closeModal = () => {
        setBookingError(null);
        dispatch(closeModifyModal());
    };

    /**
     * Handler for 'Buy Tickets' button
     */
    const onSubmitClick = async () => {
        setSpinning(true);

        const filteredCounts = filterCounts(counts);

        const pendingModification = {
            tour_id: tourId,
            tour_date: dateM?.format('YYYY-MM-DD'),
            tour_time: time,
            ticket_count_by_guest_type_id: filteredCounts,
            sales_channel: 'WEB',
            passes_by_guest_type_id: reservation.passes_by_guest_type_id,
            payment_method: reservation.payment_method,
            organization_name: reservation.organization_name,
        };

        //We need to handle mods scenarios, only for the case where site pass integration is enabled
        if (
            isReservationConfirmed &&
            !isEmpty(tour?.site_pass_integration?.facility_id)
        ) {
            if (!hasAnotherReservationForCurrentDate) {
                if (isEmpty(availableSitePassForSelectedDate)) {
                    pendingModification.site_pass_info = {
                        pass_id: sitePassForCurrentDate,
                        update: true, //We need to tell the backend to update the current site pass to a newer date
                        valid_from: dateM?.format('YYYY-MM-DD'),
                    };
                }
            }

            if (hasAnotherReservationForCurrentDate) {
                if (isEmpty(availableSitePassForSelectedDate)) {
                    pendingModification.site_pass_info = {
                        pass_id: sitePassForCurrentDate,
                        update: true, //We need to tell the backend to update the current site pass to a newer date
                        valid_from: dateM?.format('YYYY-MM-DD'),
                    };
                }
            }
        }

        // Do the thing where we send the reservation to the api
        dispatch(updateWithTicketHolderDetails(reservationId, pendingModification))
            .then(() => {
                const containerElem = document.getElementById('page-content');
                dispatch(getOrderDetailsView(reservationId));
                dispatch(CartActions.fetchCart());
                setSpinning(false);
                announce(
                    'Now closing the modify reservation date modal. Returning to order details. Your order details have been updated.'
                );
                containerElem.focus(); // Set the focus to the container after reservation is modified
                dispatch(closeModifyModal());
            })
            .catch((error) => {
                setSpinning(false);
                const serverMessage = error?.response?.data?.error;
                if (error.response.status === 400 && serverMessage) {
                    const displayError = convertServerErrorToDisplayError(serverMessage);
                    setBookingError(displayError);
                } else {
                    setBookingError(error?.message);
                }
            });
    };

    /**
     * On load, set local form state from the tour data (date picker)
     */
    useEffect(() => {
        if (tourDate && !dateM) {
            const initialDate = moment(tourDate, 'YYYY-MM-DD');
            dispatch(setDateFilter(initialDate, false));
        }
    }, [dispatch, dateM, tourDate]);

    /**
     * On load, set local form state from the tour data (time picker)
     */
    useEffect(() => {
        if (tourTime && !time) {
            setTime(tourTime);
        }
    }, [tourTime, time]);

    /**
     * Set up initial state of the guest counter.
     */
    useEffect(() => {
        if (
            reduxFacilityId &&
            !isEmpty(allowedGuestTypeIds) &&
            !isEmpty(ticketCountByGuestTypeId)
        ) {
            const initialCounts = getInitialCounts({
                allowedGuestTypeIds,
                ticketCountByGuestTypeId,
                maxTotalGuests,
                minTotalGuestsByGuestType,
            });

            setCounts(initialCounts);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reduxFacilityId, allowedGuestTypeIds, ticketCountByGuestTypeId]);

    return (
        <StyledModal
            heading="Modify Reservation"
            size="md"
            isOpen={isModifyModalOpen}
            onRequestClose={closeModal}
            shouldActionsStickInMobile
            overlayClassName="te-modify-modal"
        >
            <Stack>
                <Text>Select a new date or time, or change the number of tickets.</Text>
                <ModifyReservationForm
                    onSubmitClick={onSubmitClick}
                    bookingError={bookingError}
                    tourTimes={adjustedTourTimes}
                    time={time}
                    setTime={setTime}
                    setCounts={setCounts}
                    guestTypes={guestTypes}
                    counts={counts}
                    availableCount={availableCount}
                    isSpinning={isSpinning}
                    isTourTimesLoading={isTourTimesLoading}
                    maxTotalGuests={maxTotalGuests}
                    tourTimeOptions={timeOptions}
                    minimumCountByGuestType={minTotalGuestsByGuestType}
                    maximumCountByGuestType={maxTotalGuestsByGuestType}
                    bookThroughoutTourDuration={bookThroughoutTourDuration}
                />
            </Stack>

            <ModalActions>
                <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                    <Button appearance="tertiary" onClick={closeModal}>
                        Cancel
                    </Button>
                    <Button
                        isLoading={isSpinning}
                        onClick={onSubmitClick}
                        isDisabled={!hasBeenModified || isFormInvalid}
                    >
                        Modify Reservation
                    </Button>
                </ButtonGroup>
            </ModalActions>
        </StyledModal>
    );
}
